import { DateRange } from './types';

/**
 * Gets the start of day (00:00:00.000) for a given date
 */
export const startOfDay = (date: Date): Date => {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
};

/**
 * Gets the end of day (23:59:59.999) for a given date
 */
export const endOfDay = (date: Date): Date => {
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);
    return result;
};

/**
 * Gets the start of week for a given date
 * @param date The date to get the start of week for
 * @param weekStartsOn Day of week that starts the week (0 = Sunday, 1 = Monday, etc.)
 */
export const startOfWeek = (date: Date, weekStartsOn = 0): Date => {
    const result = new Date(date);
    const day = result.getDay();
    const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
    result.setDate(result.getDate() - diff);
    return startOfDay(result);
};

/**
 * Gets the end of week for a given date
 * @param date The date to get the end of week for
 * @param weekStartsOn Day of week that starts the week (0 = Sunday, 1 = Monday, etc.)
 */
export const endOfWeek = (date: Date, weekStartsOn = 0): Date => {
    const result = new Date(date);
    const day = result.getDay();
    const diff = (day < weekStartsOn ? -7 : 0) + 6 - (day - weekStartsOn);
    result.setDate(result.getDate() + diff);
    return endOfDay(result);
};

/**
 * Adds days to a date
 */
export const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

/**
 * Gets today's date range (start to end of day)
 */
export const getTodayRange = (): DateRange => {
    const today = new Date();
    return {
        start: startOfDay(today),
        end: endOfDay(today),
    };
};

/**
 * Gets this week's date range
 * @param weekStartsOn Day of week that starts the week (0 = Sunday, 1 = Monday, etc.)
 */
export const getThisWeekRange = (weekStartsOn = 0): DateRange => {
    const today = new Date();
    return {
        start: startOfWeek(today, weekStartsOn),
        end: endOfWeek(today, weekStartsOn),
    };
};

/**
 * Gets next week's date range
 * @param weekStartsOn Day of week that starts the week (0 = Sunday, 1 = Monday, etc.)
 */
export const getNextWeekRange = (weekStartsOn = 0): DateRange => {
    const today = new Date();
    const nextWeek = addDays(today, 7);
    return {
        start: startOfWeek(nextWeek, weekStartsOn),
        end: endOfWeek(nextWeek, weekStartsOn),
    };
};

/**
 * Checks if two dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
};

/**
 * Checks if two date ranges are equal (same start and end days)
 */
export const isRangeEqual = (range1: DateRange, range2: DateRange): boolean => {
    return isSameDay(range1.start, range2.start) && isSameDay(range1.end, range2.end);
};

/**
 * Checks if a date is within a range (inclusive)
 */
export const isDateInRange = (date: Date, range: DateRange): boolean => {
    const time = date.getTime();
    return time >= range.start.getTime() && time <= range.end.getTime();
};

/**
 * Gets all days in a month
 */
export const getDaysInMonth = (year: number, month: number): Date[] => {
    const lastDay = new Date(year, month + 1, 0);
    const days: Date[] = [];

    for (let day = 1; day <= lastDay.getDate(); day++) {
        days.push(new Date(year, month, day));
    }

    return days;
};

/**
 * Gets the month name
 */
export const getMonthName = (month: number): string => {
    const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];
    return monthNames[month];
};

/**
 * Gets the abbreviated day of week name
 */
export const getDayOfWeekName = (date: Date): string => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return dayNames[date.getDay()];
};

/**
 * Formats a date range for display (with day of week)
 */
export const formatDateRange = (range: DateRange): string => {
    // Ensure dates are in correct order
    const start = range.start <= range.end ? range.start : range.end;
    const end = range.start <= range.end ? range.end : range.start;

    const startDayOfWeek = getDayOfWeekName(start);
    const endDayOfWeek = getDayOfWeekName(end);
    const startMonth = getMonthName(start.getMonth());
    const endMonth = getMonthName(end.getMonth());
    const startDay = start.getDate();
    const endDay = end.getDate();

    if (isSameDay(start, end)) {
        return `${startDayOfWeek}, ${startMonth} ${startDay}`;
    }

    return `${startDayOfWeek}, ${startMonth} ${startDay} - ${endDayOfWeek}, ${endMonth} ${endDay}`;
};
